import { Transaction } from '../types/budget';
import Papa from 'papaparse';
import i18n from '../i18n';

// Define expected headers based on the Transaction type
const EXPECTED_HEADERS = [
  'category',
  'responsible',
  'description',
  'destination',
  'amount',
] as const;

// Helper function to safely convert a value to a number
const safeParseFloat = (value: string): number => {
  // Remove any currency symbols, spaces and convert comma decimals to dots
  const cleanValue = value
    .replace(/[^0-9,.-]/g, '') // Remove any character that's not a number, comma, dot or minus
    .replace(',', '.'); // Convert comma decimal separator to dot

  const number = parseFloat(cleanValue);

  if (isNaN(number)) {
    throw new Error(`Invalid number format: ${value}`);
  }

  return number;
};

export const importCSVFile = (file: File): Promise<Transaction[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<(typeof EXPECTED_HEADERS)[number], string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: (results) => {
        try {
          // Check for parsing errors
          if (results.errors && results.errors.length > 0) {
            const errorMessage = results.errors
              .map((error) => `Row ${error.row}: ${error.message}`)
              .join('\n');
            throw new Error(`CSV parsing errors:\n${errorMessage}`);
          }

          // Validate headers
          const headers = Object.keys(results.data[0] || {}).map((h) => h.toLowerCase());
          const missingHeaders = EXPECTED_HEADERS.filter(
            (header) => !headers.includes(header.toLowerCase())
          );

          if (missingHeaders.length > 0) {
            throw new Error(`Invalid CSV format. Missing headers: ${missingHeaders.join(', ')}`);
          }

          // Convert parsed data to Transaction objects
          const transactions: Transaction[] = results.data.map((row, index) => {
            try {
              // Create transaction object
              const transaction: Transaction = {
                id: index + 1,
                category: row.category,
                responsible: normalizeResponsible(row.responsible),
                description: row.description,
                destination: row.destination,
                amount: safeParseFloat(row.amount),
              };

              // Validate required fields
              if (!transaction.category || !transaction.responsible || !transaction.destination) {
                throw new Error('Missing required fields');
              }

              return transaction;
            } catch (error) {
              throw new Error(
                `Invalid data in row ${index + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          });

          resolve(transactions);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      },
    });
  });
};

// Helper function to normalize responsible names
const normalizeResponsible = (responsible: string): string => {
  // Convert to lowercase for case-insensitive comparison
  const lowerResponsible = responsible.toLowerCase();

  // Get all available languages from i18n
  const languages = i18n.languages || [];

  // Get "common" translation from each language
  const commonTranslations = languages.map((lang) =>
    i18n.getFixedT(lang)('defaults.responsibles.common').toLowerCase()
  );

  // Add the default "common" value to ensure it's always included
  commonTranslations.push('common');

  // If the responsible matches any translation of "common", return the standard value
  if (commonTranslations.includes(lowerResponsible)) {
    return 'common';
  }

  return responsible;
};
